import { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  CircularProgress
} from '@mui/material'
import {
  Visibility,
  VisibilityOff,
  Lock as LockIcon,
  Person as PersonIcon
} from '@mui/icons-material'
import { useLoginMutation } from '@/store/api/authApi'
import { useAppDispatch } from '@/store/hook'
import { showError, showSuccess } from '@/components/notification/NotiSlice'

// Zod schema for form validation
const loginSchema = z.object({
  userName: z
    .string()
    .min(1, 'ກະລຸນາປ້ອນຂໍ້ມູນ username'),
  password: z
    .string()
    .min(1, 'ກະລຸນາປ້ອນຂໍ້ມູນລະຫັດຜ່ານ')
    .min(6, 'ລະຫັດຜ່ານຕ້ອງມີຕົວອັກສອນຢ່າງໜ້ອຍ 6 ຕົວອັກສອນ')
})

const Login = () => {
  const [login, { isLoading }] = useLoginMutation()
  const dispatch = useAppDispatch()
  const [showPassword, setShowPassword] = useState(false)

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(loginSchema),
    mode: 'onChange', // Validate on change
    defaultValues: {
      userName: '',
      password: ''
    }
  })

  const onSubmit = async (data) => {
    try {
      const result = await login({
        userName: data.userName,
        password: data.password
      }).unwrap()
      if (result.success) {
        dispatch(showSuccess("ເຂົ້າສູ່ລະບົບສຳເລັດ"))  // Pass string directly, not object
      }

    } catch (error) {
      dispatch(showError(error.message || 'ເຂົ້າສູ່ລະບົບບໍ່ສຳເລັດ, ກະລຸນາລອງໃໝ່ອີກຄັ້ງ'))  // Pass string directly, not object

    }
  }

  const toggleShowPassword = () => {
    setShowPassword(!showPassword)
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--gray-100)',
        py: 8
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={2}
          sx={{
            p: 4,
            borderRadius: 2,
            animation: 'fadeIn 0.5s ease-in-out'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 4
            }}
          >
            <Box
              component="img"
              src="/ssc_logo.png"
              alt="SSC Logo"
              sx={{
                width: 120,
                height: 120,
                mb: 2,
                objectFit: 'contain'
              }}
            />
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                mb: 1
              }}
            >
              SSC Ticket
            </Typography>
            <Typography variant="body1" color="text.secondary">
              ກະລຸນາເຂົ້າສູ່ລະບົບເພື່ອດຳເນີນງານ
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Controller
              name="userName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  margin="normal"
                  required
                  fullWidth
                  id="userName"
                  label="User name"
                  autoFocus
                  error={!!errors.userName}
                  helperText={errors.userName?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  margin="normal"
                  required
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={toggleShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                position: 'relative'
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'ເຂົ້າສູ່ລະບົບ'
              )}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  )
}

export default Login